import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  createSeriesMarkers,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  LineSeries,
  SeriesMarker,
  Time
} from 'lightweight-charts';
import { IndicatorType } from '@shared/types';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';
import {
  isLiquidationIndicatorUpdate,
  LiquidationIndicatorUpdate,
  LiquidationIndicatorUpdatePoint
} from '../services/indicator-update.service';
import { TradingIndicatorConfig } from '../../trading-form/trading-form.types';
import {
  serializeNumericTime,
  toSortableTime
} from './utils/time-normalization.utils';
import { timeToTimestamp } from '../services/timeframe-utils';

type LiquidationPointCache = Map<string, LiquidationIndicatorUpdatePoint>;
type MarkerPluginMap = Map<number, ISeriesMarkersPluginApi<Time>>;
type LiquidationLineSeriesMap = Map<number, ISeriesApi<'Line'>[]>;
type LiquidationZigZagSeriesMap = Map<number, ISeriesApi<'Line'>>;
type TradingLiquidationIndicator = Extract<
  TradingIndicatorConfig,
  { type: 'LIQUIDATION' }
>;

const LIQUIDATION_BULLISH_COLOR = '#4DA3FF';
const LIQUIDATION_BEARISH_COLOR = '#B388FF';
const LIQUIDATION_BULLISH_LINE_COLOR = 'rgba(77, 163, 255, 0.65)';
const LIQUIDATION_BEARISH_LINE_COLOR = 'rgba(179, 136, 255, 0.65)';

const toMarkers = (
  points: LiquidationIndicatorUpdatePoint[]
): SeriesMarker<Time>[] =>
  points
    .flatMap((point) => {
      const markers: SeriesMarker<Time>[] = [];

      if (point.bullish) {
        markers.push({
          time: point.time,
          position: 'belowBar',
          shape: 'arrowUp',
          color: LIQUIDATION_BULLISH_COLOR
        });
      }

      if (point.bearish) {
        markers.push({
          time: point.time,
          position: 'aboveBar',
          shape: 'arrowDown',
          color: LIQUIDATION_BEARISH_COLOR
        });
      }

      return markers;
    })
    .sort(
      (left, right) => toSortableTime(left.time) - toSortableTime(right.time)
    );

export const useLiquidationSeries = () => {
  const {
    chartRef,
    candlestickSeriesRef,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();
  const { formState, timeframeIndex } = useChartStreamContext();

  const liquidationIndicators = useMemo(() => {
    const indicators = formState.timeframes?.[timeframeIndex]?.indicators ?? [];
    return indicators.filter(
      (indicator): indicator is TradingLiquidationIndicator =>
        indicator.type === IndicatorType.LIQUIDATION
    );
  }, [formState.timeframes, timeframeIndex]);

  const indicatorDataCacheRef = useRef<Map<number, LiquidationPointCache>>(
    new Map()
  );
  const markersPluginRef = useRef<MarkerPluginMap>(new Map());
  const markerSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const liquidationLineSeriesRefs = useRef<LiquidationLineSeriesMap>(new Map());
  const zigZagSeriesRefs = useRef<LiquidationZigZagSeriesMap>(new Map());

  const hasLiquidation = useMemo(
    () =>
      indicatorConfigs.overlay.some(
        (indicator) => indicator.type === IndicatorType.LIQUIDATION
      ),
    [indicatorConfigs.overlay]
  );

  const detachPlugins = useCallback(() => {
    markersPluginRef.current.forEach((plugin) => {
      plugin.setMarkers([]);
      plugin.detach();
    });
    markersPluginRef.current.clear();
    markerSeriesRef.current = null;
  }, []);

  const clearLinesForIndicator = useCallback(
    (indicatorIndex: number) => {
      const seriesArray = liquidationLineSeriesRefs.current.get(indicatorIndex);
      if (!seriesArray?.length) return;

      seriesArray.forEach((series) => {
        chartRef.current?.removeSeries(series);
      });

      liquidationLineSeriesRefs.current.delete(indicatorIndex);
    },
    [chartRef]
  );

  const clearZigZagForIndicator = useCallback(
    (indicatorIndex: number) => {
      const series = zigZagSeriesRefs.current.get(indicatorIndex);
      if (!series) return;

      chartRef.current?.removeSeries(series);
      zigZagSeriesRefs.current.delete(indicatorIndex);
    },
    [chartRef]
  );

  const clearAllVisualSeries = useCallback(() => {
    Array.from(liquidationLineSeriesRefs.current.keys()).forEach(
      clearLinesForIndicator
    );
    Array.from(zigZagSeriesRefs.current.keys()).forEach(
      clearZigZagForIndicator
    );
  }, [clearLinesForIndicator, clearZigZagForIndicator]);

  const ensurePlugin = useCallback(
    (indicatorIndex: number) => {
      const series = candlestickSeriesRef.current;
      if (!series) return null;

      if (markerSeriesRef.current && markerSeriesRef.current !== series) {
        detachPlugins();
      }

      if (!markersPluginRef.current.has(indicatorIndex)) {
        markersPluginRef.current.set(
          indicatorIndex,
          createSeriesMarkers(series, [])
        );
        markerSeriesRef.current = series;
      }

      return markersPluginRef.current.get(indicatorIndex) ?? null;
    },
    [candlestickSeriesRef, detachPlugins]
  );

  const mergeIndicatorData = useCallback(
    (
      indicatorIndex: number,
      points: LiquidationIndicatorUpdate['data'],
      replace: boolean
    ) => {
      const nextCache: LiquidationPointCache = replace
        ? new Map()
        : new Map(indicatorDataCacheRef.current.get(indicatorIndex) ?? []);

      points.forEach((point) => {
        nextCache.set(serializeNumericTime(point.time, 'liq'), point);
      });

      indicatorDataCacheRef.current.set(indicatorIndex, nextCache);
      return Array.from(nextCache.values()).sort(
        (left, right) => toSortableTime(left.time) - toSortableTime(right.time)
      );
    },
    []
  );

  const drawIndicatorData = useCallback(
    (indicatorIndex: number, data: LiquidationIndicatorUpdate['data']) => {
      const chart = chartRef.current;
      if (!chart) return;

      clearLinesForIndicator(indicatorIndex);
      clearZigZagForIndicator(indicatorIndex);

      // Liquidation updates use an index scoped to LIQUIDATION only
      // (not global indicators[] index), so we resolve visibility from the filtered list.
      const indicatorConfig = liquidationIndicators[indicatorIndex];
      const showLines =
        indicatorConfig?.visualization?.showLines ??
        (indicatorConfig?.visualization as { showLiquidationLines?: boolean })
          ?.showLiquidationLines ??
        (indicatorConfig?.visualization as { showSweepLines?: boolean })
          ?.showSweepLines ??
        true;
      const showZigZag = indicatorConfig?.visualization?.showZigZag ?? false;

      if (showLines) {
        const newLineSeries: ISeriesApi<'Line'>[] = [];
        const bullishLineColor = LIQUIDATION_BULLISH_LINE_COLOR;
        const bearishLineColor = LIQUIDATION_BEARISH_LINE_COLOR;

        data.forEach((point) => {
          const drawLine = (
            event:
              | LiquidationIndicatorUpdatePoint['bullishSweep']
              | LiquidationIndicatorUpdatePoint['bearishSweep']
              | LiquidationIndicatorUpdatePoint['bullishLiquidation']
              | LiquidationIndicatorUpdatePoint['bearishLiquidation'],
            color: string
          ) => {
            if (!event || typeof event.pivotPrice !== 'number') return;

            const startTime = timeToTimestamp(event.pivotTime);
            const endTime =
              timeToTimestamp(event.breakTime) ??
              timeToTimestamp(point.time) ??
              timeToTimestamp(event.confirmationTime);

            if (startTime == null || endTime == null) return;

            const series = chart.addSeries(LineSeries, {
              color,
              lineWidth: 1,
              priceLineVisible: false,
              lastValueVisible: false
            });

            series.setData([
              { time: startTime, value: event.pivotPrice },
              { time: endTime, value: event.pivotPrice }
            ]);

            newLineSeries.push(series);
          };

          drawLine(
            point.bullishLiquidation ?? point.bullishSweep,
            bullishLineColor
          );
          drawLine(
            point.bearishLiquidation ?? point.bearishSweep,
            bearishLineColor
          );
        });

        liquidationLineSeriesRefs.current.set(indicatorIndex, newLineSeries);
      }

      if (!showZigZag) return;

      const zigZagData = data
        .flatMap((point) => {
          const time = point.zigZag?.swingEnd?.time;
          const value = point.zigZag?.swingEnd?.price;
          if (time == null || typeof value !== 'number') return [];

          return [{ time, value }];
        })
        .sort(
          (left, right) =>
            toSortableTime(left.time) - toSortableTime(right.time)
        );

      if (zigZagData.length === 0) return;

      const zigZagSeries = chart.addSeries(LineSeries, {
        color: 'rgba(255, 255, 255, 0.7)',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });

      zigZagSeries.setData(zigZagData);
      zigZagSeriesRefs.current.set(indicatorIndex, zigZagSeries);
    },
    [
      chartRef,
      clearLinesForIndicator,
      clearZigZagForIndicator,
      liquidationIndicators
    ]
  );

  const applyUpdate = useCallback(
    (update: LiquidationIndicatorUpdate) => {
      const plugin = ensurePlugin(update.indicatorIndex);
      if (!plugin) return;
      const mergedData = mergeIndicatorData(
        update.indicatorIndex,
        update.data,
        update.updateType === 'snapshot'
      );

      plugin.setMarkers([]);
      plugin.setMarkers(toMarkers(mergedData));
      drawIndicatorData(update.indicatorIndex, mergedData);
    },
    [drawIndicatorData, ensurePlugin, mergeIndicatorData]
  );

  useEffect(() => {
    if (hasLiquidation) return;

    indicatorDataCacheRef.current.clear();
    detachPlugins();
    clearAllVisualSeries();
  }, [clearAllVisualSeries, detachPlugins, hasLiquidation]);

  useEffect(() => {
    indicatorDataCacheRef.current.clear();
    detachPlugins();
    clearAllVisualSeries();
  }, [clearAllVisualSeries, detachPlugins, timeframeIndex]);

  useEffect(() => {
    if (!hasLiquidation) return;

    // Visibility toggles (showLines/showZigZag) can change via form state
    // without any incoming indicator update. Re-drawing from cached points keeps
    // chart visuals in sync immediately, instead of waiting for next feed event.
    indicatorDataCacheRef.current.forEach((cache, indicatorIndex) => {
      const mergedData = Array.from(cache.values()).sort(
        (left, right) => toSortableTime(left.time) - toSortableTime(right.time)
      );

      drawIndicatorData(indicatorIndex, mergedData);
    });
  }, [drawIndicatorData, hasLiquidation, liquidationIndicators]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasLiquidation,
    indicatorType: IndicatorType.LIQUIDATION,
    isExpectedUpdate: isLiquidationIndicatorUpdate,
    applyUpdate
  });

  useEffect(() => {
    return () => {
      indicatorDataCacheRef.current.clear();
      detachPlugins();
      clearAllVisualSeries();
    };
  }, [clearAllVisualSeries, detachPlugins]);
};
